import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {BaseStorablePaneStudyableFormGroupProps} from 'samples/BaseStorablePaneStudyableFormGroup';
import CreatePlatePane, {CreatePlatePaneState} from 'samples/CreatePlatePane';
import {
  DefineExistingPlateState,
  PlateDefinitionType,
  PlateDesignState,
  PlateMapType,
} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import DefineExistingPlate from 'samples/plate-maps/annotate-fill-plate-modal/DefineExistingPlate';
import PlateSchemaSelector from 'samples/PlateSchemaSelector';
import {isGridSchemaTooBigForNewPlateFeatures} from 'samples/SampleUtil';
import {PlateSchema, PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonSize} from 'ui-library/buttons/Button';
import FieldError from 'ui-library/FieldError';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import ToggleGroup from 'ui-library/ToggleGroup';

export interface DefinePlateStepProps {
  createPlatePaneState: CreatePlatePaneState;
  setCreatePlatePaneState: (state: CreatePlatePaneState) => void;
  existingPlateState: DefineExistingPlateState;
  setExistingPlateState: (state: DefineExistingPlateState) => void;
  plateDefinitionType: PlateDefinitionType;
  setPlateDefinitionType: (type: PlateDefinitionType) => void;
  plateMapType: PlateMapType | null;
  setPlateMapType: (type: PlateMapType) => void;
  plateDesignState: PlateDesignState;
  setPlateDesignState: (state: PlateDesignState) => void;
  isFromExistingPlateDesign: boolean;
  studySearchInputProps: BaseStorablePaneStudyableFormGroupProps['studySearchInputProps'];
  showChangeWarning: boolean;
  duplicatePlateDesignName: boolean;
  allowPlateDesigns: boolean;
  allowPlateRecords: boolean;
}

/**
 * Modal step to either select parameters for creating a new plate or select an existing plate to edit.
 */
export default function DefinePlateStep({
  createPlatePaneState,
  setCreatePlatePaneState,
  existingPlateState,
  setExistingPlateState,
  plateDefinitionType,
  setPlateDefinitionType,
  plateMapType,
  setPlateMapType,
  plateDesignState,
  setPlateDesignState,
  isFromExistingPlateDesign,
  studySearchInputProps,
  showChangeWarning,
  duplicatePlateDesignName,
  allowPlateDesigns,
  allowPlateRecords,
}: DefinePlateStepProps): JSX.Element {
  function onUpdateCreatePlatePaneState(update: Partial<CreatePlatePaneState>): void {
    setCreatePlatePaneState({...createPlatePaneState, ...update});
  }

  const isCreatingPlateRecordFromDesign = plateMapType === PlateMapType.RECORD && isFromExistingPlateDesign;

  const plateSchemaSelectorProps = {
    plateSchemaType: PlateSchemaType.FIXED_PLATE,
    plateSchemaFilter: (schema: PlateSchema): boolean => !isGridSchemaTooBigForNewPlateFeatures(schema),
  };

  return (
    <div {...getTestProps({component: 'DefinePlateStep', element: 'root'})}>
      {showChangeWarning && !isFromExistingPlateDesign && (
        <div className={css(styles.warningBanner)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text="Changing schema will clear the plate map of all previously configured values"
          />
        </div>
      )}
      {!isFromExistingPlateDesign && allowPlateDesigns && allowPlateRecords && (
        <>
          <FieldLabel label="Plate map type" />
          <div
            {...getTestProps({component: 'DefinePlateStep', element: 'mapType'})}
            className={css(styles.mapTypeButtons)}
          >
            {/* TODO: Generalize client/analysis-tool/ui/SelectableCard.tsx and use it here */}
            <ToggleGroup
              // HACK: Use 'none' to model the case where neither buttom is selected, since ToggleGroup does
              // not allow `null' selection
              selection={plateMapType ?? 'none'}
              onChangeSelection={(value: PlateMapType) => {
                setPlateMapType(value);
              }}
              size={ButtonSize.MEDIUM}
              isFullWidth={true}
              options={[
                {
                  id: PlateMapType.DESIGN,
                  buttonProps: {
                    text: (
                      <span className={css(styles.mapTypeButtonText)}>
                        <p className={css(styles.mapTypeTitle)}>Plate design</p>
                        <p className={css(styles.mapTypeDesc)}>
                          Visually plan and edit the layout of your plate, before finalizing a transfer
                        </p>
                      </span>
                    ),
                  },
                },
                {
                  id: PlateMapType.RECORD,
                  buttonProps: {
                    text: (
                      <span className={css(styles.mapTypeButtonText)}>
                        <p className={css(styles.mapTypeTitle)}>Plate record</p>
                        <p className={css(styles.mapTypeDesc)}>
                          Annotate and fill a new or existing plate, saving a record of what you did
                        </p>
                      </span>
                    ),
                  },
                },
              ]}
              wrap={true}
            />
          </div>
        </>
      )}
      {(() => {
        switch (plateMapType) {
          case PlateMapType.DESIGN:
            return (
              <div className={css(styles.plateDesignContainer)}>
                <FormGroup className={css(styles.column)}>
                  <TextField
                    label="Name"
                    autoFocus={true}
                    placeholder="Plate design name..."
                    value={plateDesignState.name || ''}
                    onChange={(e) => {
                      setPlateDesignState({...plateDesignState, name: e.target.value});
                    }}
                    hasError={duplicatePlateDesignName}
                    dataTestAttrs={{component: 'DefinePlateStep', element: 'plateDesignNameTextInput'}}
                  />
                  {duplicatePlateDesignName && <FieldError message="Name already in use" compact={true} />}
                </FormGroup>
                <FormGroup className={css(styles.column)}>
                  <FieldLabel isRequired={true} label="Schema" labelStyle={FieldLabel.STYLES.LABEL} />
                  <PlateSchemaSelector
                    {...plateSchemaSelectorProps}
                    onChange={(schema: PlateSchema | null) => {
                      setPlateDesignState({...plateDesignState, plateSchema: schema ?? null});
                    }}
                    registryId={undefined}
                    selectedPlateSchemaId={plateDesignState?.plateSchema?.id ?? null}
                    disabled={isFromExistingPlateDesign}
                    disabledMessage="Schema cannot be changed for an existing plate design"
                  />
                </FormGroup>
              </div>
            );
          case PlateMapType.RECORD:
            return (
              <>
                <FieldLabel label="Annotate and fill" />
                <div
                  {...getTestProps({component: 'DefinePlateStep', element: 'definitionType'})}
                  className={css(styles.definitionTypeButtons)}
                >
                  <ToggleGroup
                    selection={plateDefinitionType}
                    onChangeSelection={(value: PlateDefinitionType) => {
                      setPlateDefinitionType(value);
                    }}
                    size={ButtonSize.MEDIUM}
                    isFullWidth={true}
                    options={[
                      {
                        id: PlateDefinitionType.CREATE,
                        buttonProps: {text: 'New well plate'},
                      },
                      {
                        id: PlateDefinitionType.EXISTING,
                        buttonProps: {text: 'Existing well plate'},
                      },
                    ]}
                    wrap={true}
                  />
                </div>
                {plateDefinitionType === PlateDefinitionType.CREATE ? (
                  <CreatePlatePane
                    onUpdate={onUpdateCreatePlatePaneState}
                    registryId={createPlatePaneState.registry!.id}
                    plateSchemaSelectorProps={plateSchemaSelectorProps}
                    studySearchInputProps={studySearchInputProps}
                    disablePlateSchemaAndRegistrySelectors={isCreatingPlateRecordFromDesign}
                    disabledPlateSchemaSelectorMessage="Plate records use the schema of the plate design from which they were created"
                    disabledRegistrySelectorMessage="Plate records inherit their registry from the schema of the plate design from which they were created"
                    {...createPlatePaneState}
                  />
                ) : (
                  <DefineExistingPlate
                    existingPlateState={existingPlateState}
                    setExistingPlateState={setExistingPlateState}
                    plateConstraintProps={
                      isCreatingPlateRecordFromDesign
                        ? {requireEmptyPlate: true, plateSchema: plateDesignState.plateSchema!}
                        : {requireEmptyPlate: false, plateSchema: null}
                    }
                  />
                )}
              </>
            );
          case null:
            return null;
          default:
            throw new Error(`Unexpected plate map type: ${plateMapType}`);
        }
      })()}
    </div>
  );
}

const SINGLE_COLUMN_LAYOUT_WIDTH = '466px';

const styles = StyleSheet.create({
  warningBanner: {
    marginBottom: 16,
  },
  definitionTypeButtons: {
    width: '300px',
    marginBottom: '16px',
  },
  mapTypeButtons: {
    marginBottom: '16px',
    width: SINGLE_COLUMN_LAYOUT_WIDTH,
  },
  mapTypeButtonText: {
    textAlign: 'left',
  },
  mapTypeTitle: {
    fontSize: '13px',
    fontWeight: 600,
    marginBottom: '6px',
  },
  mapTypeDesc: {
    fontSize: '11px',
    lineHeight: '14px',
    marginBottom: 0,
  },
  plateDesignContainer: {
    display: 'flex',
    gap: 24,
  },
  column: {
    flex: 1,
  },
});
